﻿import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { FormProvider, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@mui/material";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  QrCode,
  Printer,
  Eye,
  CheckCircle,
  ClipboardList,
  Check,
  Copy,
  Code,
  Download,
  Globe,
  XCircle,
  AlertCircle,
  MessageSquare,
  GripVertical,
  Loader,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ServicesSettingsSkeleton from "./skeletons/ServicesSettingsSkeleton";
import { deleteConfirmationMessage, nhsAhtWeightTemplateId } from "@/data/constantStrings";
import { MessageActionType } from "@/enum/MessageActionType";
import {
  Template,
  TemplatePreviewModalProps,
} from "@/lib/settings/templatesData";
import {
  useServiceSettingsData,
  Service,
  ServiceSchedule,
  ServicesSettingsTabProps,
  ServiceFormPanelProps,
  ServiceQRCodePanelProps,
  SetupCheck,
} from "@/lib/settings/servicesData";
import { getCompanyMarketplaceApps } from "@/dispatchers/marketAppDispacher";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import QRCode from "qrcode";

const DAY_NAMES_CONST = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
const DAY_LABELS_CONST = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function SortableServiceCard({
  service,
  index,
  isDarkMode,
  onShowQRCode,
  onEditService,
  handleEditService,
  confirmDelete,
  handleDeleteService,
  handleDuplicateService
}: {
  service: Service;
  index: number;
  isDarkMode: boolean;
  onShowQRCode: (service: Service) => void;
  onEditService: (service: Service) => void;
  handleEditService: (
    service: Service,
    onEditService: (service: Service) => void,
  ) => void;
   handleDuplicateService: (
    serviceId: string
  ) => void;
  confirmDelete: (onConfirm: () => void, itemType?: string) => void;
  handleDeleteService: (serviceID: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const [isDuplicatingService, setIsDuplicatingService] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-4 transition-colors duration-300 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      } ${isDragging ? "shadow-lg" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1 gap-3">
          <button
            className={`cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
              isDarkMode ? "text-gray-400" : "text-gray-400"
            }`}
            title="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-4 h-4" />
          </button>

          <span
            className={`text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center ${
              isDarkMode
                ? "bg-blue-900/40 text-blue-300"
                : "bg-blue-50 text-blue-600"
            }`}
            title={`Position ${index + 1} on booking platform`}
          >
            {index + 1}
          </span>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div
                className={`w-4 h-4 rounded-full border transition-colors duration-300 ${
                  isDarkMode ? "border-gray-600" : "border-gray-300"
                }`}
                style={{ backgroundColor: service?.color }}
              />
              <h3
                className={`font-medium transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {service?.name}
              </h3>
              <Badge variant={service?.active ? "default" : "secondary"}>
                {service?.active ? "Active" : "Inactive"}
              </Badge>
            </div>
            <div
              className={`flex items-center gap-4 text-sm transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <span>Duration: {service?.duration} min</span>
              <span>Price: £{service?.price}</span>
              {service?.showOnPublicCalendar && <span>Public</span>}
              {service?.roomRequired && <span>Room Required</span>}
            </div>
          </div>
        </div>

        {service?.serviceType !== 1 && service?.serviceType !== 3 && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onShowQRCode(service)}
              title="Generate QR Code"
            >
              <QrCode className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleEditService(service, onEditService)}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
           {isDuplicatingService ? (
          <Loader className="w-4 h-4 animate-spin" />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
                    setIsDuplicatingService(true);
                    try {
                      await handleDuplicateService(service._id);
                    } finally {
                      setIsDuplicatingService(false);
                    }
                  }}
            title="Duplicate service"
          >
            <Copy className="w-4 h-4" />
          </Button>
        )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                confirmDelete(
                  () => handleDeleteService(service._id),
                  deleteConfirmationMessage + "service",
                )
              }
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Services Settings Tab Component
export function ServicesSettingsTab({
  onAddService,
  onEditService,
  onShowQRCode,
  confirmDelete,
}: ServicesSettingsTabProps) {
  const {
    services,
    isDarkMode,
    handleAddService,
    handleEditService,
    isPharmacyServiceLoading,
    handleDeleteService,
    showIframeModal,
    setShowIframeModal,
    iframeCopied,
    generateIframeCode,
    handleCopyIframe,
    handlePublicUrlClick,
    showChecklistModal,
    setShowChecklistModal,
    isChecklistLoading,
    defaultCompanyChecklist,
    companyId,
    updateServiceSorting,
    refreshServicesFromDispatcher,
    allPharmacies,
    getRoomsByPharmacyId,
    handleDuplicateService
  } = useServiceSettingsData();

  useEffect(() => {
    if (companyId) {
      getCompanyMarketplaceApps(companyId);
    }
  }, [companyId]);

  const [urlCopied, setUrlCopied] = useState(false);
  const getSafeCount = (value: unknown): number => {
    const parsedValue = Number(value);
    return parsedValue;
  };

  const serviceCount = getSafeCount(defaultCompanyChecklist?.serviceCount);
  const userCount = getSafeCount(defaultCompanyChecklist?.userCount);
  const activeLocationsCount = getSafeCount(
    defaultCompanyChecklist?.activeLocationsCount,
  );
  const roomsCount = getSafeCount(defaultCompanyChecklist?.roomsCount);

  const companyData = defaultCompanyChecklist?.company || {};
  const companyName =
    companyData?.companyName || companyData?.CompanyName || null;
  const companyAddress =
    companyData?.companyAddress || companyData?.CompanyAddress || null;
  const contactNumber =
    companyData?.contactNumber || companyData?.ContactNumber || null;
  const companyOwnerEmailAddress =
    companyData?.companyOwnerEmailAddress || null;
  const allServicesConfigured =
    defaultCompanyChecklist?.allServicesConfigured || null;

  const isValuePresentForCompany = (value: unknown) =>
    value !== null && value !== undefined && String(value).trim().length > 0;

  const isCompanyComplete =
    isValuePresentForCompany(companyName) &&
    isValuePresentForCompany(companyAddress) &&
    isValuePresentForCompany(contactNumber) &&
    isValuePresentForCompany(companyOwnerEmailAddress);

  const pluralize = (count: number, singular: string, plural: string) =>
    `${count} ${count === 1 ? singular : plural}`;

  const serviceDescription =
    serviceCount > 0
      ? `${pluralize(serviceCount, "public service", "public services")} configured`
      : `Atleast 1 Service Setup is Required`;

  const usersDescription =
    userCount > 0
      ? `${pluralize(userCount, "active team member", "active team members")} configured`
      : "AtLeast one active team member is required";

  const companyDescription = isCompanyComplete
    ? "Organisation name, address, phone & email are set"
    : "Complete your organisation name,address,phone and email in general settings";

  const locationsDescription =
    activeLocationsCount > 0
      ? `${pluralize(activeLocationsCount, "active location", "active locations")} configured`
      : "AtLeast one active location is required";

  const roomsDescription =
    roomsCount > 0
      ? `${pluralize(roomsCount, "room", "rooms")} set up within ${activeLocationsCount === 1 ? "a location" : "locations"}`
      : "AtLeast one room is required";

  const serviceAvailabilityDescription = allServicesConfigured
    ? "Services have their availability schedule configured"
    : "Service(s) lack availability schedule.";

  const setupChecks = [
    {
      id: "services",
      label: "Services",
      description: serviceDescription,
      passed: serviceCount > 0,
      settingsTab: "Services",
    },
    {
      id: "users",
      label: "Users",
      description: usersDescription,
      passed: userCount > 0,
      settingsTab: "Members",
    },
    {
      id: "company",
      label: "Company",
      description: companyDescription,
      passed: isCompanyComplete,
      settingsTab: "General",
    },
    {
      id: "locations",
      label: "Locations",
      description: locationsDescription,
      passed: activeLocationsCount > 0,
      settingsTab: "Locations",
    },
    {
      id: "rooms",
      label: "Rooms",
      description: roomsDescription,
      passed: roomsCount > 0,
      settingsTab: "Locations",
    },
    {
      id: "service-availability",
      label: "Service Availability",
      description: serviceAvailabilityDescription,
      passed: allServicesConfigured,
      settingsTab: "Services",
    },
    {
      id: "user-availability",
      label: "User Availability",
      description: "Team members have their availability configured",
      passed: true,
      settingsTab: "Members",
    },
    {
      id: "location-availability",
      label: "Location Availability",
      description: "Rooms within locations have availability configured",
      passed: true,
      settingsTab: "Locations",
    },
  ];

  const allChecksPassed = setupChecks.every((c) => c.passed);
  const passedCount = setupChecks.filter((c) => c.passed).length;
  const bookingUrl = `${import.meta.env.VITE_BOOKING_URL}${companyId}`;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(bookingUrl);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const filteredServices = useMemo(
    () => services.filter((s) => s?.serviceType !== 1 && s?.serviceType !== 3),
    [services],
  );
  const [orderedServices, setOrderedServices] = useState<Service[]>([]);

  const publicServices = useMemo(
    () => orderedServices.filter((s) => s.showOnPublicCalendar),
    [orderedServices],
  );
  const internalServices = useMemo(
    () => orderedServices.filter((s) => !s.showOnPublicCalendar),
    [orderedServices],
  );

  useEffect(() => {
    const sorted = [...filteredServices].sort((a, b) => {
      const aNum = (a as any).sortingNumber ?? 0;
      const bNum = (b as any).sortingNumber ?? 0;
      return aNum - bNum;
    });

    setOrderedServices(sorted);
  }, [filteredServices]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedServices.findIndex((s) => s._id === active.id);
    const newIndex = orderedServices.findIndex((s) => s._id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(orderedServices, oldIndex, newIndex);

    const newOrderWithSorting = newOrder.map((service, index) => ({
      ...service,
      sortingNumber: index + 1,
    }));

    setOrderedServices(newOrderWithSorting);

    const payload = {
      Services: newOrderWithSorting.map((service, index) => ({
        ServiceId: service._id,
        SortingNumber: index + 1,
      })),
    };

    await updateServiceSorting(payload);
  };

  if (isPharmacyServiceLoading) {
    return <ServicesSettingsSkeleton />;
  }
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Services
        </h2>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handlePublicUrlClick}
            disabled={isChecklistLoading}
            // onClick={() => setShowIframeModal(true)}
          >
            <Globe className="w-4 h-4 mr-2" />
            {isChecklistLoading ? "Checking..." : "Public URL"}
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-slate-900 hover:bg-slate-800"
            onClick={() => {
              handleAddService(onAddService);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </div>
      </div>

      {/* Services List */}
      <div className="space-y-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedServices.map((service) => service._id)}
            strategy={verticalListSortingStrategy}
          >
            {/* Public services — shown on booking platform */}
            {publicServices.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Public Services ({publicServices.length})
                  </h3>
                  <div
                    className={`flex items-center gap-1.5 text-xs rounded-md px-2 py-1 ${
                      isDarkMode
                        ? "bg-blue-900/30 text-blue-300"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    <GripVertical className="w-3 h-3" />
                    <span>Drag to set booking platform order</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {publicServices.map((service, index) => (
                    <SortableServiceCard
                      key={service._id}
                      service={service}
                      index={index}
                      isDarkMode={isDarkMode}
                      onShowQRCode={onShowQRCode}
                      onEditService={onEditService}
                      handleEditService={handleEditService}
                      confirmDelete={confirmDelete}
                      handleDeleteService={handleDeleteService}
                      handleDuplicateService={handleDuplicateService}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Internal-only services — not shown on booking platform */}
            {internalServices.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <h3
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Internal Only ({internalServices.length})
                  </h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      isDarkMode
                        ? "bg-gray-700 text-gray-400"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    Not shown on booking platform
                  </span>
                </div>
                <div className="space-y-3">
                  {internalServices.map((service, index) => (
                    <SortableServiceCard
                      key={service._id}
                      service={service}
                      index={index}
                      isDarkMode={isDarkMode}
                      onShowQRCode={onShowQRCode}
                      onEditService={onEditService}
                      handleEditService={handleEditService}
                      confirmDelete={confirmDelete}
                      handleDeleteService={handleDeleteService}
                      handleDuplicateService={handleDuplicateService}
                    />
                  ))}
                </div>
              </div>
            )}
          </SortableContext>
        </DndContext>
      </div>
      <Dialog open={showIframeModal} onOpenChange={setShowIframeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Embed Services on Your Website</DialogTitle>
            <DialogDescription>
              Copy the code below to embed your services booking page on your
              website.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 space-y-2 border rounded-lg">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                  Direct Booking Link
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyUrl}
                  className="h-8"
                >
                  {urlCopied ? (
                    <>
                      <Check className="w-3 h-3 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 mr-1" />
                      Copy URL
                    </>
                  )}
                </Button>
              </div>
              <div className="relative">
                <pre className="p-4 overflow-x-auto text-sm border rounded-lg bg-gray-50 dark:bg-gray-900">
                  <code className="text-wrap">{bookingUrl}</code>
                </pre>
              </div>
              <div
                className={`rounded-md p-3 ${
                  isDarkMode
                    ? "bg-violet-900/20 border border-violet-800/40"
                    : "bg-violet-50 border border-violet-100"
                }`}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare
                    className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                      isDarkMode ? "text-violet-400" : "text-violet-600"
                    }`}
                  />
                  <div>
                    <p
                      className={`text-xs font-medium ${
                        isDarkMode ? "text-violet-300" : "text-violet-700"
                      }`}
                    >
                      Great for SMS &amp; campaigns
                    </p>
                    <p
                      className={`text-xs mt-0.5 ${
                        isDarkMode ? "text-violet-400" : "text-violet-600"
                      }`}
                    >
                      Use this URL in SMS messages, email newsletters, social
                      media posts, QR codes, or printed materials to let
                      patients book appointments directly.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-2 border rounded-lg">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">iFrame Code</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyIframe}
                  className="h-8"
                >
                  {iframeCopied ? (
                    <>
                      <Check className="w-3 h-3 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 mr-1" />
                      Copy Code
                    </>
                  )}
                </Button>
              </div>
              <div className="relative">
                <pre className="p-4 overflow-x-auto text-sm border rounded-lg bg-gray-50 dark:bg-gray-900">
                  <code className="text-wrap">{generateIframeCode()}</code>
                </pre>
              </div>

              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
                <h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">
                  How to use:
                </h4>
                <ul className="ml-4 space-y-1 text-sm text-blue-800 list-disc dark:text-blue-300">
                  <li>Copy the iFrame code above</li>
                  <li>
                    Paste it into your website's HTML where you want the booking
                    page to appear
                  </li>
                  <li>
                    Customers will be able to view and book your services
                    directly from your website
                  </li>
                  <li>
                    You can adjust the width and height attributes to fit your
                    website's layout
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Setup Checklist Modal */}
      <Dialog open={showChecklistModal} onOpenChange={setShowChecklistModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {allChecksPassed ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-500" />
              )}
              Setup Checklist
            </DialogTitle>
            <DialogDescription>
              {allChecksPassed
                ? "All setup requirements are complete. Your booking page is ready to embed."
                : `Complete all ${setupChecks.length} requirements before exposing your booking iFrame. (${passedCount}/${setupChecks.length} done)`}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-2">
            {setupChecks.map((check) => (
              <div
                key={check.id}
                className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                  check.passed
                    ? isDarkMode
                      ? "border-green-800/50 bg-green-900/20"
                      : "border-green-200 bg-green-50"
                    : isDarkMode
                      ? "border-red-800/50 bg-red-900/20"
                      : "border-red-100 bg-red-50"
                }`}
              >
                <div className="mt-0.5 shrink-0">
                  {check.passed ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-medium ${
                        check.passed
                          ? isDarkMode
                            ? "text-green-300"
                            : "text-green-800"
                          : isDarkMode
                            ? "text-red-300"
                            : "text-red-800"
                      }`}
                    >
                      {check.label}
                    </span>
                    {!check.passed && (
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                          isDarkMode
                            ? "bg-gray-700 text-gray-400"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {check.settingsTab} settings
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-xs mt-0.5 ${
                      check.passed
                        ? isDarkMode
                          ? "text-green-400"
                          : "text-green-700"
                        : isDarkMode
                          ? "text-red-400"
                          : "text-red-700"
                    }`}
                  >
                    {check.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowChecklistModal(false)}
            >
              Close
            </Button>
            {allChecksPassed && (
              <Button
                className="text-white bg-slate-900 hover:bg-slate-800"
                onClick={() => {
                  setShowChecklistModal(false);
                  setShowIframeModal(true);
                }}
              >
                <Globe className="w-4 h-4 mr-2" />
                View Public URL
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScheduleEditor({
  schedule,
  onChange,
  isDarkMode = false,
}: {
  schedule: ServiceSchedule;
  onChange: (updated: ServiceSchedule) => void;
  isDarkMode?: boolean;
}) {
  const addBlock = (day: keyof ServiceSchedule) => {
    onChange({
      ...schedule,
      [day]: {
        ...schedule[day],
        timeBlocks: [
          ...schedule[day].timeBlocks,
          { from: "09:00", to: "17:00" },
        ],
      },
    });
  };
  const removeBlock = (day: keyof ServiceSchedule, idx: number) => {
    onChange({
      ...schedule,
      [day]: {
        ...schedule[day],
        timeBlocks: schedule[day].timeBlocks.filter((_, i) => i !== idx),
      },
    });
  };
  const updateBlock = (
    day: keyof ServiceSchedule,
    idx: number,
    field: "from" | "to",
    value: string,
  ) => {
    onChange({
      ...schedule,
      [day]: {
        ...schedule[day],
        timeBlocks: schedule[day].timeBlocks.map((b, i) =>
          i === idx ? { ...b, [field]: value } : b,
        ),
      },
    });
  };

  return (
    <div className="space-y-2">
      {DAY_NAMES_CONST.map((day, index) => (
        <div
          key={day}
          className={`border rounded p-3 transition-colors duration-300 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <div className="flex items-center gap-3">
            <Switch
              checked={schedule[day].enabled}
              onCheckedChange={(checked) => {
                const updatedDay = { ...schedule[day], enabled: checked };
                if (!checked) updatedDay.timeBlocks = [];
                if (checked && updatedDay.timeBlocks.length === 0)
                  updatedDay.timeBlocks = [{ from: "09:00", to: "17:00" }];
                onChange({ ...schedule, [day]: updatedDay });
              }}
            />
            <Label className="font-medium text-sm min-w-[80px]">
              {DAY_LABELS_CONST[index]}
            </Label>
            {schedule[day].enabled && (
              <div className="flex flex-wrap items-center flex-1 gap-2">
                {schedule[day].timeBlocks.map((block, bi) => (
                  <div key={bi} className="flex items-center gap-1">
                    <Input
                      type="time"
                      value={block.from}
                      onChange={(e) =>
                        updateBlock(day, bi, "from", e.target.value)
                      }
                      className="w-20 h-8 text-sm"
                    />
                    <span
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      -
                    </span>
                    <Input
                      type="time"
                      value={block.to}
                      onChange={(e) =>
                        updateBlock(day, bi, "to", e.target.value)
                      }
                      className="w-20 h-8 text-sm"
                    />
                    {schedule[day].timeBlocks.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBlock(day, bi)}
                        className="w-6 h-6 p-0"
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addBlock(day)}
                  className="h-6 px-2 text-xs"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Sortable Pharmacist Card Component (for drag-and-drop priority ordering)
function SortablePharmacistCard({
  email,
  fullName,
  index,
  isDarkMode,
  otherLocationNames,
  onRemove,
}: {
  email: string;
  fullName: string;
  index: number;
  isDarkMode: boolean;
  otherLocationNames: string | null;
  onRemove: (email: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: email });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 select-none transition-colors ${
        isDarkMode
          ? "border-blue-600 bg-blue-900/30"
          : "border-blue-500 bg-blue-50"
      } ${isDragging ? "shadow-lg" : ""}`}
    >
      <button
        className={`cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isDarkMode ? "text-gray-400" : "text-gray-400"
        }`}
        title="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <span
        className={`text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center ${
          isDarkMode
            ? "bg-blue-900/40 text-blue-300"
            : "bg-blue-50 text-blue-600"
        }`}
        title={`Priority ${index + 1}`}
      >
        {index + 1}
      </span>

      <div className="flex flex-col min-w-0 flex-1">
        <span
          className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {fullName}
        </span>
        {otherLocationNames && (
          <span
            className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {otherLocationNames}
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(email);
        }}
        className={`p-1 rounded transition-colors ${
          isDarkMode
            ? "hover:bg-gray-700 text-gray-400 hover:text-red-400"
            : "hover:bg-gray-100 text-gray-400 hover:text-red-500"
        }`}
        title="Remove pharmacist"
      >
        <XCircle className="w-4 h-4" />
      </button>
    </div>
  );
}

// Service Form Panel Component
export function ServiceFormPanel({
  service,
  templates,
  onSave,
  onBack,
}: ServiceFormPanelProps) {
  const {
    dayLabels,
    dayNames,
    membersData,
    isDarkMode,
    currentPreConsultTemplates,
    currentAiTemplates,
    isAhtWeightManagementInstalled,
    methods,
    watchedValues,
    handleGetAllRooms,
    getRoomById,
    convertRoomAvailabilityToSchedule,
    handleAddTimeBlock,
    handleUpdateSchedule,
    handleRemoveTimeBlock,
    handleUpdateTimeBlock,
    onSubmit,
    isSubmitting,
    allPharmacies,
    getRoomsByPharmacyId,
    removeServiceFromPharmacy,
    companyId,
  } = useServiceSettingsData({ initialService: service, onBack, onSave });

  const [selectedRoomForSchedule, setSelectedRoomForSchedule] = useState<
    string | null
  >(null);
  const [configuringSiteId, setConfiguringSiteId] = useState<string | null>(
    null,
  );
  const [removingLocId, setRemovingLocId] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const isEditMode = !!service?._id;
  const originalLocIds: string[] = service?.assignedLocationIds || [];
  const drillRef = useRef<HTMLDivElement | null>(null);
  // Update form schedule when room is selected
  useEffect(() => {
    if (selectedRoomForSchedule) {
      const roomData = getRoomById(selectedRoomForSchedule);
      if (roomData) {
        const roomSchedule = convertRoomAvailabilityToSchedule(roomData);
        methods.setValue("schedule", roomSchedule, { shouldDirty: true });
      }
    }
  }, [selectedRoomForSchedule]);
  const oneTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pharmacistSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handlePharmacistDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !configuringSiteId) return;

    const locId = configuringSiteId;
    const byLoc = {
      ...(methods.getValues("assignedPharmacistsByLocation") || {}),
    };
    const current = byLoc[locId] || [];

    const oldIndex = current.indexOf(active.id as string);
    const newIndex = current.indexOf(over.id as string);

    if (oldIndex === -1 || newIndex === -1) return;

    byLoc[locId] = arrayMove(current, oldIndex, newIndex);
    methods.setValue("assignedPharmacistsByLocation", byLoc, {
      shouldDirty: true,
    });
  };

  // Inside ServiceFormPanel, before the main return statement:

  const DEFAULT_LOCATION_SCHEDULE: ServiceSchedule = {
    monday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    tuesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    wednesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    thursday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    friday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    saturday: { enabled: false, timeBlocks: [] },
    sunday: { enabled: false, timeBlocks: [] },
  };

  const convertServiceAvailabilityToSchedule = (
    availability: any[] | undefined,
  ): ServiceSchedule => {
    const schedule: ServiceSchedule = {
      monday: { enabled: false, timeBlocks: [] },
      tuesday: { enabled: false, timeBlocks: [] },
      wednesday: { enabled: false, timeBlocks: [] },
      thursday: { enabled: false, timeBlocks: [] },
      friday: { enabled: false, timeBlocks: [] },
      saturday: { enabled: false, timeBlocks: [] },
      sunday: { enabled: false, timeBlocks: [] },
    };

    if (!availability || !Array.isArray(availability)) {
      return schedule;
    }

    const dayMap: { [key: number]: keyof ServiceSchedule } = {
      0: "sunday",
      1: "monday",
      2: "tuesday",
      3: "wednesday",
      4: "thursday",
      5: "friday",
      6: "saturday",
    };

    availability.forEach((dayAvailability: any) => {
      const dayName = dayMap[dayAvailability?.dayOfWeek];
      if (!dayName) return;
      if (dayAvailability.slots && dayAvailability.slots.length > 0) {
        schedule[dayName] = {
          enabled: true,
          timeBlocks: dayAvailability.slots.map((slot: any) => ({
            from: slot.startTime,
            to: slot.endTime,
          })),
        };
      }
    });

    return schedule;
  };

  // Helper functions for location-based assignments
  const toggleLocationConfig = (locationId: string) => {
    const current = methods.getValues("assignedLocationIds") || [];
    if (current.includes(locationId)) {
      // Removing location — clean up rooms, pharmacists, schedule for this location
      const loc = (allPharmacies || []).find(
        (l: any) => l.pharmacyID === locationId,
      );
      const locRoomIds = (loc?.pharmacyRooms || []).map((r: any) => r._id);

      const currentRoomIds = methods.getValues("assignedRoomIds") || [];
      const currentPharmByLoc =
        methods.getValues("assignedPharmacistsByLocation") || {};
      const currentSchedByLoc = methods.getValues("scheduleByLocation") || {};

      const { [locationId]: _p, ...restPharm } = currentPharmByLoc;
      const { [locationId]: _s, ...restSched } = currentSchedByLoc;

      methods.setValue(
        "assignedLocationIds",
        current.filter((id) => id !== locationId),
        { shouldDirty: true },
      );
      methods.setValue(
        "assignedRoomIds",
        currentRoomIds.filter((id: string) => !locRoomIds.includes(id)),
        { shouldDirty: true },
      );
      methods.setValue("assignedPharmacistsByLocation", restPharm, {
        shouldDirty: true,
      });
      methods.setValue("scheduleByLocation", restSched, { shouldDirty: true });
    } else {
      methods.setValue("assignedLocationIds", [...current, locationId], {
        shouldDirty: true,
      });
    }
  };

  const toggleRoomForLocation = (roomId: string) => {
    const current = methods.getValues("assignedRoomIds") || [];
    if (current.includes(roomId)) {
      methods.setValue(
        "assignedRoomIds",
        current.filter((id: string) => id !== roomId),
        { shouldDirty: true },
      );
    } else {
      methods.setValue("assignedRoomIds", [...current, roomId], {
        shouldDirty: true,
      });
    }
  };

  const togglePharmacistForLocation = (locationId: string, email: string) => {
    const byLoc = {
      ...(methods.getValues("assignedPharmacistsByLocation") || {}),
    };
    const current = byLoc[locationId] || [];
    byLoc[locationId] = current.includes(email)
      ? current.filter((id) => id !== email)
      : [...current, email];
    methods.setValue("assignedPharmacistsByLocation", byLoc, {
      shouldDirty: true,
    });
  };

  const updateScheduleForLocation = (
    locationId: string,
    updated: ServiceSchedule,
  ) => {
    const current = methods.getValues("scheduleByLocation") || {};
    methods.setValue(
      "scheduleByLocation",
      { ...current, [locationId]: updated },
      { shouldDirty: true },
    );
  };

  // Scroll to top when entering drill-down
  useEffect(() => {
    if (!configuringSiteId) return;
    let el: HTMLElement | null = drillRef.current;
    while (el) {
      const styles = window.getComputedStyle(el);
      const oy = styles.overflowY;
      if (
        (oy === "auto" || oy === "scroll") &&
        el.scrollHeight > el.clientHeight
      ) {
        el.scrollTop = 0;
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo({ top: 0 });
  }, [configuringSiteId]);

  const resetLocationState = (locId: string) => {
    const currentLocIds = methods.getValues("assignedLocationIds") || [];
    methods.setValue(
      "assignedLocationIds",
      currentLocIds.filter((id) => id !== locId),
      { shouldDirty: true },
    );

    const newPharmacists = {
      ...(methods.getValues("assignedPharmacistsByLocation") || {}),
    };
    delete newPharmacists[locId];
    methods.setValue("assignedPharmacistsByLocation", newPharmacists, {
      shouldDirty: true,
    });

    const newSchedule = { ...(methods.getValues("scheduleByLocation") || {}) };
    delete newSchedule[locId];
    methods.setValue("scheduleByLocation", newSchedule, { shouldDirty: true });

    const loc = (allPharmacies || []).find((p: any) => p.pharmacyID === locId);
    const locRoomIds = (loc?.pharmacyRooms || []).map((r: any) => r._id);
    const currentRoomIds = methods.getValues("assignedRoomIds") || [];
    methods.setValue(
      "assignedRoomIds",
      currentRoomIds.filter((id: string) => !locRoomIds.includes(id)),
      { shouldDirty: true },
    );
  };

  const handleRemoveClick = (locId: string) => {
    setRemovingLocId(locId);
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = async () => {
    if (!removingLocId) return;

    const isOriginallyConfigured =
      isEditMode && originalLocIds.includes(removingLocId);
    if (isOriginallyConfigured && service?._id) {
      await removeServiceFromPharmacy({
        ServiceID: service._id,
        PharmacyID: removingLocId,
        CompanyID: companyId,
      });
    }

    resetLocationState(removingLocId);
    setConfiguringSiteId(null);
    setShowRemoveConfirm(false);
    setRemovingLocId(null);
  };

  // === DRILL-DOWN VIEW ===
  if (configuringSiteId) {
    const loc = allPharmacies.find(
      (l: any) => l.pharmacyID === configuringSiteId,
    );

    if (loc) {
      const locId = loc.pharmacyID;
      const locRooms = loc.pharmacyRooms || [];
      const assignedRoomIds = methods.getValues("assignedRoomIds") || [];
      const assignedPharmacists =
        (methods.getValues("assignedPharmacistsByLocation") || {})[locId] || [];
      const siteSchedule =
        (methods.getValues("scheduleByLocation") || {})[locId] ||
        DEFAULT_LOCATION_SCHEDULE;
      const pharmacists = (membersData || []).filter(
        (m: any) => m.isActive !== false,
      );

      const assignedPharmacistList = assignedPharmacists
        .map((email: string) =>
          pharmacists.find((m: any) => m.emailAddress === email),
        )
        .filter(Boolean);

      const availablePharmacistList = pharmacists.filter(
        (m: any) => !assignedPharmacists.includes(m.emailAddress),
      );

      return (
        <div ref={drillRef} className="p-6 space-y-6">
          {/* Drill-down header */}
          <div
            className={`sticky top-0 z-10 flex items-center gap-3 border-b pb-4 transition-colors duration-300 ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setConfiguringSiteId(null)}
              className={
                isDarkMode
                  ? "text-gray-300 hover:text-white hover:bg-gray-700"
                  : ""
              }
            >
              ← Back to Service
            </Button>
            <div>
              <h2
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {loc.pharmacyName || loc.name}
              </h2>
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {loc.pharmacyAddress || loc.address || ""}
              </p>
            </div>
          </div>

          <div className="p-6 space-y-8">
            {/* Rooms */}
            <div>
              <h4
                className={`font-medium mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Rooms
              </h4>
              {locRooms.length === 0 ? (
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  No rooms configured for this site yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {locRooms.map((room: any) => {
                    const roomId = room._id || room.id;
                    const assigned = assignedRoomIds.includes(roomId);
                    return (
                      <div
                        key={roomId}
                        onClick={() => toggleRoomForLocation(roomId)}
                        role="checkbox"
                        aria-checked={assigned}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter")
                            toggleRoomForLocation(roomId);
                        }}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer select-none transition-colors ${
                          assigned
                            ? isDarkMode
                              ? "border-blue-600 bg-blue-900/30"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                              ? "border-gray-600 hover:border-gray-500 bg-gray-800"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div
                          className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                            assigned
                              ? "border-blue-500 bg-blue-500"
                              : isDarkMode
                                ? "border-gray-500"
                                : "border-gray-400"
                          }`}
                        >
                          {assigned && (
                            <svg
                              className="w-2.5 h-2.5 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                        <span
                          className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {room.roomName || room.name}
                        </span>
                        {room.active === false && (
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded ml-auto ${isDarkMode ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                          >
                            Inactive
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pharmacists */}
            <div>
              <h4
                className={`font-medium mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Pharmacists
              </h4>
              {pharmacists.length === 0 ? (
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  No active pharmacists available.
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Assigned Pharmacists — Draggable priority list */}
                  {assignedPharmacistList.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h5
                          className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Assigned ({assignedPharmacistList.length})
                        </h5>
                        <div
                          className={`flex items-center gap-1.5 text-xs rounded-md px-2 py-1 ${
                            isDarkMode
                              ? "bg-blue-900/30 text-blue-300"
                              : "bg-blue-50 text-blue-600"
                          }`}
                        >
                          <GripVertical className="w-3 h-3" />
                          <span>Drag to set priority order</span>
                        </div>
                      </div>

                      <DndContext
                        sensors={pharmacistSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handlePharmacistDragEnd}
                      >
                        <SortableContext
                          items={assignedPharmacists}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {assignedPharmacistList.map(
                              (m: any, index: number) => {
                                const memberPharmacies = m.pharmacies || [];
                                const isInCurrentLocation =
                                  memberPharmacies.some(
                                    (p: any) => p.pharmacyID === locId,
                                  );
                                const otherLocationNames = !isInCurrentLocation
                                  ? memberPharmacies
                                      .map(
                                        (p: any) =>
                                          p.pharmacyName || p.pharmacyID,
                                      )
                                      .join(", ")
                                  : null;

                                return (
                                  <SortablePharmacistCard
                                    key={m.emailAddress}
                                    email={m.emailAddress}
                                    fullName={m.fullName}
                                    index={index}
                                    isDarkMode={isDarkMode}
                                    otherLocationNames={otherLocationNames}
                                    onRemove={(email) =>
                                      togglePharmacistForLocation(locId, email)
                                    }
                                  />
                                );
                              },
                            )}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}

                  {/* Available Pharmacists — Checkboxes to assign */}
                  {availablePharmacistList.length > 0 && (
                    <div>
                      {assignedPharmacistList.length > 0 && (
                        <h5
                          className={`text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          Available ({availablePharmacistList.length})
                        </h5>
                      )}
                      <div className="space-y-2">
                        {availablePharmacistList.map((m: any) => {
                          const email = m.emailAddress;
                          const memberPharmacies = m.pharmacies || [];
                          const isInCurrentLocation = memberPharmacies.some(
                            (p: any) => p.pharmacyID === locId,
                          );
                          const otherLocationNames = !isInCurrentLocation
                            ? memberPharmacies
                                .map(
                                  (p: any) => p.pharmacyName || p.pharmacyID,
                                )
                                .join(", ")
                            : null;

                          return (
                            <div
                              key={email}
                              onClick={() =>
                                togglePharmacistForLocation(locId, email)
                              }
                              role="checkbox"
                              aria-checked={false}
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === " " || e.key === "Enter")
                                  togglePharmacistForLocation(locId, email);
                              }}
                              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer select-none transition-colors ${
                                isDarkMode
                                  ? "border-gray-600 hover:border-gray-500 bg-gray-800"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                              }`}
                            >
                              <div
                                className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                                  isDarkMode
                                    ? "border-gray-500"
                                    : "border-gray-400"
                                }`}
                              />

                              <div className="flex flex-col min-w-0">
                                <span
                                  className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {m.fullName}
                                </span>
                                {otherLocationNames && (
                                  <span
                                    className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {otherLocationNames}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Schedule */}
            <div>
              <h4
                className={`font-medium mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Schedule
              </h4>
              <ScheduleEditor
                schedule={siteSchedule}
                onChange={(updated) =>
                  updateScheduleForLocation(locId, updated)
                }
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          {/* Availability Period */}
            <div>
            <h4 className={`font-medium mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Availability Period
            </h4>
            <p className={`text-xs mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              Set when this service starts and stops being available to book at this site. Leave blank for no limit.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDarkMode ? "text-gray-200" : "text-gray-900"}>Start date</Label>
                <Input
                  type="date"
                  value={
                    methods.watch(`assignedDatesForLocations.${locId}.serviceStartDate`) || ""
                  }
                  onChange={(e) => {
                    const current = methods.getValues("assignedDatesForLocations") || {};
                    const newStartDate = e.target.value;
                    const existingEndDate = current[locId]?.serviceEndDate || "";

                    // If the existing end date is before the new start date, clear it
                    const correctedEndDate =
                      existingEndDate && newStartDate && existingEndDate < newStartDate
                        ? ""
                        : existingEndDate;

                    methods.setValue(
                      "assignedDatesForLocations",
                      {
                        ...current,
                        [locId]: {
                          ...(current[locId] || {}),
                          serviceStartDate: newStartDate,
                          serviceEndDate: correctedEndDate,
                        },
                      },
                      { shouldDirty: true }
                    );
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDarkMode ? "text-gray-200" : "text-gray-900"}>Stop date</Label>
                <Input
                  type="date"
                  value={
                    methods.watch(`assignedDatesForLocations.${locId}.serviceEndDate`) || ""
                  }
                  min={
                    methods.watch(`assignedDatesForLocations.${locId}.serviceStartDate`) || undefined
                  }
                  onChange={(e) => {
                    const current = methods.getValues("assignedDatesForLocations") || {};
                    const newEndDate = e.target.value;
                    const startDate = current[locId]?.serviceStartDate || "";
                    const correctedEndDate = startDate && newEndDate && newEndDate < startDate ? "" : newEndDate;
                    methods.setValue(
                      "assignedDatesForLocations",
                      {
                        ...current,
                        [locId]: {
                          ...(current[locId] || {}),
                          serviceEndDate: correctedEndDate,
                        },
                      },
                      { shouldDirty: true }
                    );
                  }}
                />
            </div>
          </div>
          {(() => {
            const startDate = methods.watch(`assignedDatesForLocations.${locId}.serviceStartDate`);
            const endDate = methods.watch(`assignedDatesForLocations.${locId}.serviceEndDate`);
            if (startDate && endDate && endDate < startDate) {
              return (
                <p className="text-xs mt-2 text-red-500">
                  Stop date must be on or after the start date.
                </p>
              );
            }
            return null;
          })()}
        </div>
          <div
            className={`flex justify-between items-center pt-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleRemoveClick(locId)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove from service
            </Button>
            <Button
              type="button"
              className="text-white bg-slate-900 hover:bg-slate-800"
              onClick={() => setConfiguringSiteId(null)}
            >
              Done
            </Button>
          </div>
          {showRemoveConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div
                className={`rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 ${
                  isDarkMode
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-900"
                }`}
              >
                <h3 className="text-base font-semibold mb-2">
                  Remove from Service
                </h3>
                <p
                  className={`text-sm mb-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Are you sure you want to remove this location from the
                  service? All configured rooms, pharmacists and schedule for
                  this location will be cleared.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowRemoveConfirm(false);
                      setRemovingLocId(null);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      isDarkMode
                        ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmRemove}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
  }
  return (
    <div className="p-6 space-y-6">
      {/* Header with Back Button */}
      <div
        className={`sticky top-0 z-10 flex items-center gap-3 border-b pb-4 transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          ← Back
        </Button>
        <h2
          className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {service?._id ? "Edit Service" : "Add New Service"}
        </h2>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={methods?.handleSubmit(onSubmit)}>
          <div className="p-6 space-y-8">
            {/* Details Section */}

            <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    className={`transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Service Name
                  </Label>

                  <Input
                    {...methods.register("name", {
                      required: "Service name is required",
                      minLength: { value: 2, message: "Minimum 2 characters" },
                    })}
                    placeholder="Enter service name"
                  />
                  {methods.formState.errors.name && (
                    <p className="text-sm text-red-500">
                      {methods.formState.errors.name.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label
                    className={`transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Price (£)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      {...methods.register("price", {
                        min: {
                          value: 0,
                          message: "Price must be non-negative",
                        },
                        setValueAs: (v) => (v === "" ? 0 : parseFloat(v)),
                      })}
                      placeholder="0.00"
                      className="flex-1"
                    />

                    <Controller
                      name="serviceChargesType"
                      control={methods.control}
                      render={({ field }) => (
                        <Select
                          value={field.value?.toString() || ""}
                          onValueChange={(value) =>
                            field.onChange(parseInt(value))
                          }
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Booking Fee</SelectItem>
                            <SelectItem value="2">Consultation Fee</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  {methods.formState.errors.price && (
                    <p className="text-sm text-red-500">
                      {methods.formState.errors.price.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Controller
                    name="showOnPublicCalendar"
                    control={methods.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />

                  <Label
                    className={`transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Show on public calendar
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Controller
                    name="roomRequired"
                    control={methods.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />

                  <Label
                    className={`transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Room Required
                  </Label>
                </div>
              </div>
            </div>
            {/* Booking Options - Only show if public calendar is enabled */}
            {watchedValues.showOnPublicCalendar && (
              <div>
                <h4
                  className={`font-medium mb-4 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Booking Options
                </h4>
                <div className="space-y-4">
                  <div
                    className={`border rounded-lg p-4 space-y-4 transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-blue-900/20 border-blue-700"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    {/* Group Bookings */}
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Controller
                            name="allowGroupBookings"
                            control={methods.control}
                            render={({ field }) => (
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            )}
                          />
                          <div>
                            <Label
                              className={`transition-colors duration-300 ${
                                isDarkMode ? "text-blue-200" : "text-blue-900"
                              }`}
                            >
                              Allow Group Bookings
                            </Label>
                            <p
                              className={`text-xs mt-1 transition-colors duration-300 ${
                                isDarkMode ? "text-blue-300" : "text-blue-700"
                              }`}
                            >
                              Enable multiple appointment slots per person
                            </p>
                          </div>
                        </div>
                      </div>
                      {watchedValues.allowGroupBookings && (
                        <div className="mt-3 space-y-2">
                          <Label
                            className={`transition-colors duration-300 ${
                              isDarkMode ? "text-blue-200" : "text-blue-900"
                            }`}
                          >
                            Maximum Group Size
                          </Label>
                          <Input
                            type="number"
                            min={2}
                            max={100}
                            defaultValue={2}
                            onKeyDown={(e) => {
                              if (
                                ["e", "E", "+", "-", ".", ","].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                            onInput={(e) => {
                              const input = e.target as HTMLInputElement;
                              const raw = input.value;

                              // Allow empty while user deletes
                              if (raw === "") {
                                methods.setValue(
                                  "maxGroupSize",
                                  undefined as any,
                                );
                                return;
                              }

                              // Clear any previous "1" timer
                              if (oneTimeoutRef.current) {
                                clearTimeout(oneTimeoutRef.current);
                                oneTimeoutRef.current = null;
                              }

                              // If user just typed "1", allow it *temporarily*
                              if (raw === "1") {
                                methods.setValue("maxGroupSize", 1 as any);

                                oneTimeoutRef.current = setTimeout(() => {
                                  // After delay, if it's still "1", force it to "2"
                                  if (input.value === "1") {
                                    input.value = "2";
                                    methods.setValue("maxGroupSize", 2);
                                  }
                                }, 900); // tweak delay if you want
                                return;
                              }

                              let value = parseInt(raw, 10);

                              if (isNaN(value)) {
                                value = 2;
                              }

                              // Clamp only upper bound here; lower bound is handled by "1" logic + blur
                              if (value > 100) value = 100;

                              input.value = value.toString();
                              methods.setValue("maxGroupSize", value);
                            }}
                            onBlur={(e) => {
                              const input = e.target as HTMLInputElement;
                              const raw = input.value;

                              if (oneTimeoutRef.current) {
                                clearTimeout(oneTimeoutRef.current);
                                oneTimeoutRef.current = null;
                              }

                              if (raw === "") {
                                input.value = "2";
                                methods.setValue("maxGroupSize", 2);
                                return;
                              }

                              let value = parseInt(raw, 10);

                              if (isNaN(value) || value < 2) {
                                value = 2;
                              } else if (value > 100) {
                                value = 100;
                              }

                              input.value = value.toString();
                              methods.setValue("maxGroupSize", value);
                            }}
                            {...methods.register("maxGroupSize", {
                              required: "Max group size is required",
                              valueAsNumber: true,
                              min: {
                                value: 2,
                                message: "Group Size must be at least 2",
                              },
                              max: {
                                value: 100,
                                message: "Group Size cannot exceed 100",
                              },
                            })}
                            className={`max-w-xs ${
                              isDarkMode
                                ? "bg-gray-800 border-gray-600 text-white"
                                : ""
                            }`}
                          />
                          <p
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-blue-300" : "text-blue-700"
                            }`}
                          >
                            How many people can book at the same time
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Pre-payments */}
                    <div className="flex items-center space-x-2">
                      <Controller
                        name="enablePrePayments"
                        control={methods.control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                      <div>
                        <Label
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-blue-200" : "text-blue-900"
                          }`}
                        >
                          Enable pre-payments for online bookings
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Duration and Colour - Always show */}
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    className={`transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Duration (mins)
                  </Label>
                  <Input
                    type="number"
                    {...methods.register("duration", {
                      required: "Duration is required",
                      valueAsNumber: true,
                      min: {
                        value: 1,
                        message: "Duration must be at least 1 minute",
                      },
                    })}
                  />
                  {methods.formState.errors.duration && (
                    <p className="text-sm text-red-500">
                      {methods.formState.errors.duration.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label
                    className={`transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Colour
                  </Label>
                  <Controller
                    name="color"
                    control={methods.control}
                    rules={{ required: "Colour is required" }}
                    render={({ field }) => <Input type="color" {...field} />}
                  />

                  {methods.formState.errors.color && (
                    <p className="text-sm text-red-500">
                      {methods.formState.errors.color.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Assignments */}
            <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Assignments
              </h4>

              {/* Sites / Locations */}
              {allPharmacies.filter((p: any) => p.active === true).length >
                0 && (
                <div className="space-y-2">
                  <Label
                    className={`transition-colors duration-300 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                  >
                    Sites
                  </Label>
                  <p
                    className={`text-xs transition-colors duration-300 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Configure this service for each of your sites
                  </p>
                  <div className="space-y-2 mt-2">
                    {allPharmacies
                      .filter((p: any) => p.active === true)
                      .map((loc: any) => {
                        const locId = loc.pharmacyID;

                        const assignedLocationIds =
                          methods.getValues("assignedLocationIds") || [];
                        const isConfigured =
                          assignedLocationIds.includes(locId);
                        const locRooms = loc.pharmacyRooms || [];

                        const assignedRoomsHere = (
                          methods.getValues("assignedRoomIds") || []
                        ).filter((rid: string) =>
                          locRooms.some((r: any) => (r._id || r.id) === rid),
                        );
                        const assignedPharmHere =
                          (methods.getValues("assignedPharmacistsByLocation") ||
                            {})[locId] || [];
                        const siteSchedule = (methods.getValues(
                          "scheduleByLocation",
                        ) || {})[locId];
                        const scheduledDays = siteSchedule
                          ? DAY_NAMES_CONST.filter(
                              (d) => siteSchedule[d]?.enabled,
                            ).map(
                              (d) => d.charAt(0).toUpperCase() + d.slice(1, 3),
                            )
                          : [];

                        const openConfigure = () => {
                          if (!isConfigured) {
                            const current =
                              methods.getValues("assignedLocationIds") || [];
                            methods.setValue(
                              "assignedLocationIds",
                              [...current, locId],
                              { shouldDirty: true },
                            );
                          }
                          const currentByLoc =
                            methods.getValues("scheduleByLocation") || {};
                          if (!currentByLoc[locId]) {
                            methods.setValue(
                              "scheduleByLocation",
                              {
                                ...currentByLoc,
                                [locId]: DEFAULT_LOCATION_SCHEDULE,
                              },
                              { shouldDirty: true },
                            );
                          }
                          setConfiguringSiteId(locId);
                        };

                        return (
                          <div
                            key={locId}
                            onClick={openConfigure}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") openConfigure();
                            }}
                            className={`rounded-lg border cursor-pointer transition-colors ${
                              isConfigured
                                ? isDarkMode
                                  ? "border-blue-600 bg-blue-900/20 hover:bg-blue-900/30"
                                  : "border-blue-200 bg-blue-50/50 hover:bg-blue-50"
                                : isDarkMode
                                  ? "border-gray-700 bg-gray-800 hover:border-gray-600"
                                  : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-3 px-3 py-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <div
                                    className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {loc.pharmacyName || loc.name}
                                  </div>
                                  <span
                                    className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                                      isConfigured
                                        ? isDarkMode
                                          ? "bg-blue-900/60 text-blue-300"
                                          : "bg-blue-100 text-blue-700"
                                        : isDarkMode
                                          ? "bg-gray-700 text-gray-400"
                                          : "bg-gray-100 text-gray-500"
                                    }`}
                                  >
                                    {isConfigured
                                      ? "Configured"
                                      : "Not configured"}
                                  </span>
                                </div>
                                {isConfigured &&
                                (assignedRoomsHere.length > 0 ||
                                  assignedPharmHere.length > 0 ||
                                  scheduledDays.length > 0) ? (
                                  <div
                                    className={`text-xs truncate ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                                  >
                                    {[
                                      assignedRoomsHere.length > 0 &&
                                        `${assignedRoomsHere.length} room${assignedRoomsHere.length > 1 ? "s" : ""}`,
                                      assignedPharmHere.length > 0 &&
                                        `${assignedPharmHere.length} pharmacist${assignedPharmHere.length > 1 ? "s" : ""}`,
                                      scheduledDays.length > 0 &&
                                        scheduledDays.join(", "),
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </div>
                                ) : (
                                  <div
                                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {loc.pharmacyAddress || loc.address || ""}
                                  </div>
                                )}
                              </div>
                              <span
                                className={`flex-shrink-0 text-xs font-medium ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                              >
                                {isConfigured ? "Edit →" : "Configure →"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation & Cancellation */}
            <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Confirmation & Cancellation
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="emailConfirmationTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="Email confirmation template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="email"
                      actionType={MessageActionType.EmailConfirmation}
                    />
                  )}
                />

                <Controller
                  name="emailCancellationTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="Email cancellation template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="email"
                      actionType={MessageActionType.EmailCancellation}
                    />
                  )}
                />

                <Controller
                  name="smsConfirmationTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="SMS confirmation template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="sms"
                      actionType={MessageActionType.SMSConfirmation}
                    />
                  )}
                />

                <Controller
                  name="smsCancellationTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="SMS cancellation template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="sms"
                      actionType={MessageActionType.SMSCancellation}
                    />
                  )}
                />
              </div>
            </div>

            {/* Reschedule */}

            <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Reschedule
              </h4>

              <div className="grid grid-cols-2 gap-4">
                {/* Email Reschedule */}
                <Controller
                  name="emailRescheduleTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="Email reschedule template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="email"
                      actionType={MessageActionType.EmailReschedule}
                    />
                  )}
                />

                {/* SMS Reschedule */}
                <Controller
                  name="smsRescheduleTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="SMS reschedule template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="sms"
                      actionType={MessageActionType.SmsReschedule}
                    />
                  )}
                />
              </div>
            </div>

            {/* Reminders */}
            <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Reminders
              </h4>

              <div className="grid grid-cols-2 gap-4">
                {/* Email Reminder */}
                <Controller
                  name="emailReminderTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="Email reminder template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="email"
                      actionType={MessageActionType.EmailReminder}
                    />
                  )}
                />

                {/* SMS Reminder */}
                <Controller
                  name="smsReminderTemplate"
                  control={methods.control}
                  render={({ field }) => (
                    <TemplateSelector
                      label="SMS reminder template"
                      value={field.value}
                      onChange={field.onChange}
                      templates={templates}
                      templateType="sms"
                      actionType={MessageActionType.SMSReminder}
                    />
                  )}
                />
              </div>
            </div>

            {/* Template Configuration - Side by Side Layout */}
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {/* Pre-Consult Form */}
              <div>
                <h4
                  className={`font-medium mb-4 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Pre-Consult Form
                </h4>
                <div className="space-y-4">
                  <div
                    className={`border rounded-lg p-4 transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-blue-900/30 border-blue-700"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <ClipboardList
                        className={`w-5 h-5 mt-0.5 transition-colors duration-300 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      />
                      <div>
                        <h5
                          className={`font-medium mb-1 transition-colors duration-300 ${
                            isDarkMode ? "text-blue-200" : "text-blue-900"
                          }`}
                        >
                          What is a Pre-Consult Form?
                        </h5>
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-blue-300" : "text-blue-700"
                          }`}
                        >
                          This will send a link to the patient to fill out this
                          form before arriving for their appointment. Patients
                          can complete medical history, symptoms, and other
                          relevant information in advance, making your
                          consultation more efficient and comprehensive.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Select Pre-Consult Template</Label>

                    <Controller
                      name="preConsultTemplate"
                      control={methods.control}
                      render={({ field }) => (
                        <Select
                          value={field.value || "none"}
                          onValueChange={(value) =>
                            field.onChange(value === "none" ? "" : value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a pre-consult template (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            {currentPreConsultTemplates.length > 0 && (
                              <SelectItem key="none" value="none">
                                No default template
                              </SelectItem>
                            )}
                            {currentPreConsultTemplates.map((template) => (
                              <SelectItem
                                key={template._id}
                                value={template._id}
                              >
                                <div className="font-medium">
                                  {template.templateName}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <p
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Create custom templates in Settings ? Pre-Consult
                      Templates
                    </p>
                  </div>

                  {watchedValues.preConsultTemplate &&
                    watchedValues.preConsultTemplate !== "none" && (
                      <div
                        className={`border rounded-lg p-3 transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-green-900/30 border-green-700"
                            : "bg-green-50 border-green-200"
                        }`}
                      >
                        <div
                          className={`flex items-center gap-2 transition-colors duration-300 ${
                            isDarkMode ? "text-green-300" : "text-green-800"
                          }`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            Pre-consult form will be sent to patients when they
                            book this service
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Default Consultation Template */}
              <div>
                <h4
                  className={`font-medium mb-4 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Default Consultation Template
                </h4>
                <div className="space-y-4">
                  <div
                    className={`border rounded-lg p-4 transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-purple-900/30 border-purple-700"
                        : "bg-purple-50 border-purple-200"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <ClipboardList
                        className={`w-5 h-5 mt-0.5 transition-colors duration-300 ${
                          isDarkMode ? "text-purple-400" : "text-purple-600"
                        }`}
                      />
                      <div>
                        <h5
                          className={`font-medium mb-1 transition-colors duration-300 ${
                            isDarkMode ? "text-purple-200" : "text-purple-900"
                          }`}
                        >
                          What is a Default Consultation Template?
                        </h5>
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-purple-300" : "text-purple-700"
                          }`}
                        >
                          This template will be automatically used as the
                          default structure for consultations for this service.
                          It provides a standardized format for documenting
                          patient visits, ensuring consistency and thoroughness
                          in your consultation notes.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      className={`transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      Select Default Consultation Template
                    </Label>

                    <Controller
                      name="defaultConsultationTemplate"
                      control={methods.control}
                      render={({ field }) => (
                        <Select
                          value={field.value || "none"}
                          onValueChange={(value) =>
                            field.onChange(value === "none" ? "" : value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a consultation template (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              No default template
                            </SelectItem>
                            {currentAiTemplates?.map((template) => (
                              <SelectItem
                                key={template?._id}
                                value={template?._id}
                                disabled={template?.forceUserSignOff} // Disable if forceUserSignOff is true
                              >
                                {template?.templateName}
                                {template?.isDefault && " (Default)"}
                                {template?.forceUserSignOff && (
                                  <span className="px-2 ml-2 text-xs text-red-500 bg-red-100 rounded-full">
                                    Not compliant
                                  </span>
                                )}
                              </SelectItem>
                            ))}
                            {isAhtWeightManagementInstalled && (
                              <SelectItem
                                key={nhsAhtWeightTemplateId}
                                value={nhsAhtWeightTemplateId}
                              >
                                Weight Management (AHT)
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <p
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Create custom templates in Settings ? Templates ?
                      Consultation
                    </p>
                  </div>
                  {methods.getValues("defaultConsultationTemplate") && (
                    <div
                      className={`border rounded-lg p-3 transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-green-900/30 border-green-700"
                          : "bg-green-50 border-green-200"
                      }`}
                    >
                      <div
                        className={`flex items-center gap-2 transition-colors duration-300 ${
                          isDarkMode ? "text-green-300" : "text-green-800"
                        }`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {(() => {
                            const selectedId = methods.getValues(
                              "defaultConsultationTemplate",
                            );
                            const selectedTemplate = currentAiTemplates.find(
                              (template) => template._id === selectedId,
                            );
                            const templateName =
                              selectedTemplate?.templateName ||
                              (selectedId === nhsAhtWeightTemplateId
                                ? "Weight Management (AHT)"
                                : null);
                            return templateName
                              ? `"${templateName}" will be used as the default consultation template for this service`
                              : "Default consultation template will be applied to this service";
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Schedules */}
            {/* <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Schedules
                {selectedRoomForSchedule && (
                  <span
                    className={`ml-2 text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    ({getRoomById(selectedRoomForSchedule)?.roomName || "Room"})
                  </span>
                )}
              </h4>
              {selectedRoomForSchedule && (
                <div
                  className={`mb-4 p-3 rounded border transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-blue-900/20 border-blue-700"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <p
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-blue-200" : "text-blue-800"
                    }`}
                  >
                    ? Room availability loaded. Edit slots below to customize
                    the service schedule. You can change times, add new slots,
                    or remove existing ones.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedRoomForSchedule(null)}
                    className={`mt-2 text-sm underline transition-colors duration-300 ${
                      isDarkMode
                        ? "text-blue-300 hover:text-blue-200"
                        : "text-blue-600 hover:text-blue-800"
                    }`}
                  >
                    Clear room selection
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 gap-3">
                {dayNames.map((day, index) => (
                  <div
                    key={day}
                    className={`border rounded p-3 transition-colors duration-300 ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Switch
                        checked={
                          watchedValues.schedule[day as keyof ServiceSchedule]
                            .enabled
                        }
                        onCheckedChange={(checked) =>
                          handleUpdateSchedule(
                            day as keyof ServiceSchedule,
                            "enabled",
                            checked,
                          )
                        }
                      />
                      <Label className="font-medium text-sm min-w-[80px]">
                        {dayLabels[index]}
                      </Label>

                      {watchedValues.schedule[day as keyof ServiceSchedule]
                        .enabled && (
                        <div className="flex flex-wrap items-center flex-1 gap-2">
                          {watchedValues.schedule[
                            day as keyof ServiceSchedule
                          ].timeBlocks.map((block, blockIndex) => (
                            <div
                              key={blockIndex}
                              className="flex items-center gap-1"
                            >
                              <Input
                                type="time"
                                value={block.from}
                                onChange={(e) =>
                                  handleUpdateTimeBlock(
                                    day as keyof ServiceSchedule,
                                    blockIndex,
                                    "from",
                                    e.target.value,
                                  )
                                }
                                className="w-20 h-8 text-xs"
                              />
                              <span
                                className={`text-xs transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                -
                              </span>
                              <Input
                                type="time"
                                value={block.to}
                                onChange={(e) =>
                                  handleUpdateTimeBlock(
                                    day as keyof ServiceSchedule,
                                    blockIndex,
                                    "to",
                                    e.target.value,
                                  )
                                }
                                className="w-20 h-8 text-xs"
                              />
                              {watchedValues.schedule[
                                day as keyof ServiceSchedule
                              ].timeBlocks.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleRemoveTimeBlock(
                                      day as keyof ServiceSchedule,
                                      blockIndex,
                                    )
                                  }
                                  className="w-6 h-6 p-0"
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleAddTimeBlock(day as keyof ServiceSchedule)
                            }
                            className="h-6 px-2 text-xs"
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
          </div>

          {/* Action Buttons */}
          <div
            className={`flex gap-2 justify-end pt-4 border-t transition-colors duration-300 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <Button type="button" variant="outline" onClick={onBack}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-slate-900 hover:bg-slate-800"
              disabled={isSubmitting} // Add this
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? "Saving..." : "Save Service"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

// Service QR Code Panel Component
export function ServiceQRCodePanel({
  service,
  onBack,
}: ServiceQRCodePanelProps) {
  const [urlCopied, setUrlCopied] = useState(false);
  const [iframeCopied, setIframeCopied] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [isQRLoading, SetisQRLoading] = useState(true);
  const [QRchecklistData, setQRChecklistData] = useState<any>(null);
  // const canvasRef = useRef<HTMLCanvasElement>(null);
  const { companyId, handleGetServiceQRCheckList } = useServiceSettingsData();
  const generateServiceURL = (service: Service): string => {
    // Service objects use `_id` (from backend). Fall back to `id` if present.
    const id = (service as any)?._id || (service as any)?.id || "";
    return `${import.meta.env.VITE_BOOKING_URL}${companyId}/${id}`;
  };

  const generateIframeCodeWithServiceId = (service: Service) => {
    const serviceid = (service as any)?._id || (service as any)?.id || "";
    const baseUrl = `${import.meta.env.VITE_BOOKING_URL}${companyId}/${serviceid}`;
    return `<iframe src="${baseUrl}" width="100%" height="600" frameborder="0"></iframe>`;
  };

  const canvasRef = useCallback(
    async (canvas: HTMLCanvasElement | null) => {
      if (!canvas) return;
      try {
        const url = generateServiceURL(service);
        await QRCode.toCanvas(canvas, url, {
          width: 300,
          margin: 2,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
        });
        const dataUrl = canvas.toDataURL("image/png");
        setQrCodeDataUrl(dataUrl);
      } catch (error) {
        console.error("Error generating QR code:", error);
      }
    },
    [service],
  );

  const handleCopyUrlWithServideId = (service: Service, copyType: string) => {
    if (copyType === "iframe") {
      navigator.clipboard.writeText(generateIframeCodeWithServiceId(service));
      setIframeCopied(true);
      setTimeout(() => setIframeCopied(false), 2000);
    } else {
      navigator.clipboard.writeText(generateServiceURL(service));
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  };

  const generateQRData = (service: Service): string => {
    const serviceUrl = generateServiceURL(service);
    return serviceUrl;
  };

  const handleCopyUrl = () => {
    const url = generateServiceURL(service);
    navigator.clipboard.writeText(url);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement("a");
      link.download = `${service.name
        .replace(/\s+/g, "-")
        .toLowerCase()}-qr-code.png`;
      link.href = qrCodeDataUrl;
      link.click();
    }
  };

  useEffect(() => {
    // const generateQR = async () => {
    //   try {
    //     const url = generateServiceURL(service);
    //     const canvas = canvasRef.current;
    //     if (canvas) {
    //       await QRCode.toCanvas(canvas, url, {
    //         width: 300,
    //         margin: 2,
    //         color: {
    //           dark: "#000000",
    //           light: "#FFFFFF",
    //         },
    //       });
    //       const dataUrl = canvas.toDataURL("image/png");
    //       setQrCodeDataUrl(dataUrl);
    //     }
    //   } catch (error) {
    //     console.error("Error generating QR code:", error);
    //   }
    // };

    const fetchChecklist = async () => {
      try {
        const data = await handleGetServiceQRCheckList(service._id);
        setQRChecklistData(data);
      } catch (err) {
        console.error(err);
        setQRChecklistData(null);
      } finally {
        setTimeout(() => {
          SetisQRLoading(false);
        }, 300);
      }
    };

    fetchChecklist();
  }, [service]);

  useEffect(() => {
    const allChecksPassed = serviceChecks.every((c) => c.passed);
    setIsSetupComplete(allChecksPassed);
  }, [QRchecklistData]);

  const serviceChecks: SetupCheck[] = [
    {
      id: "service-public",
      label: "Public Service",
      //  description: "This service is visible on the public booking page",
      description: QRchecklistData?.serviceOnlineVisibility
        ? "This service is visible on the public booking page"
        : "This service must be set to show on the public calendar",
      passed: QRchecklistData?.serviceOnlineVisibility,
      settingsTab: "Services",
    },
    {
      id: "service-schedule",
      label: "Service Schedule",
      //  description: "This service is visible on the public booking page",

      description: QRchecklistData?.serviceDaysScheduled
        ? "This service has availability days configured"
        : "Set at least one availability day for this service",
      passed: QRchecklistData?.serviceDaysScheduled,
      settingsTab: "Services",
    },
    {
      id: "service-rooms",
      label: "Assigned Rooms",
      description:
        QRchecklistData?.serviceRoomAssigned > 0
          ? // ?  "rooms are assigned"
            `${QRchecklistData?.serviceRoomAssigned} room${QRchecklistData?.serviceRoomAssigned === 1 ? "" : "s"} assigned`
          : `${QRchecklistData?.serviceRoomAssigned} room${QRchecklistData?.serviceRoomAssigned === 1 ? "" : "s"} assigned`,
      passed:
        !QRchecklistData?.isRoomRequired ||
        QRchecklistData?.serviceRoomAssigned > 0,
      settingsTab: "Services",
    },
    {
      id: "service-pharmacists",
      label: "Assigned Pharmacists",
      description:
        QRchecklistData?.servicePharmacistAssigned > 0
          ? // ?  "pharmacists are assigned"
            `${QRchecklistData?.servicePharmacistAssigned} pharmacist${QRchecklistData?.servicePharmacistAssigned === 1 ? "" : "s"} assigned`
          : "Assign at least one pharmacist to this service",
      passed: QRchecklistData?.servicePharmacistAssigned > 0,
      settingsTab: "Services",
    },
  ];

  const passedCount = serviceChecks.filter((c) => c.passed).length;

  const qrData = generateQRData(service);

  const handlePrint = () => {
    if (!qrCodeDataUrl) return;

    const serviceUrl = generateServiceURL(service);

    const printHtml = `
    <html>
      <head>
        <title>QR Code - ${service.name}</title>
        <style>
          * {
            box-sizing: border-box;
          }
          body {
            margin: 0;
            padding: 24px;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            background: #f5f5f5;
          }
          .page {
            max-width: 480px;
            margin: 0 auto;
            padding: 24px;
            background: #ffffff;
            border-radius: 16px;
            border: 1px solid #e5e5e5;
            text-align: center;
          }
          h1 {
            font-size: 20px;
            margin: 0 0 8px;
            color: #111827;
          }
          .subtitle {
            font-size: 13px;
            color: #6b7280;
            margin-bottom: 16px;
          }
          .qr-wrapper {
            display: inline-flex;
            padding: 12px;
            border-radius: 16px;
            border: 1px solid #111827;
            margin: 8px 0 16px;
          }
          .qr-wrapper img {
            width: 220px;
            height: 220px;
            display: block;
          }
          .service-card {
            margin-top: 8px;
            padding: 10px 12px;
            border-radius: 12px;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            font-size: 13px;
            color: #111827;
          }
          .service-card-title {
            font-weight: 600;
            margin-bottom: 4px;
          }
          .service-card-meta {
            color: #4b5563;
            font-size: 12px;
          }
          .url-label {
            margin-top: 14px;
            font-size: 11px;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.06em;
          }
          .url-box {
            margin-top: 4px;
            padding: 8px 10px;
            font-size: 11px;
            border-radius: 8px;
            border: 1px dashed #d1d5db;
            background: #f9fafb;
            color: #111827;
            word-break: break-all;
          }
          .footer-note {
            margin-top: 16px;
            font-size: 11px;
            color: #9ca3af;
          }
          @media print {
            body {
              background: #ffffff;
              padding: 0;
            }
            .page {
              box-shadow: none;
              border: none;
              max-width: none;
              width: 100%;
              height: auto;
              border-radius: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <h1>${service.name}</h1>
          <div class="subtitle">Scan this QR code to book this service</div>

          <div class="qr-wrapper">
            <img src="${qrCodeDataUrl}" alt="QR code for ${service.name}" />
          </div>

          <div class="service-card">
            <div class="service-card-title">${service.name}</div>
            <div class="service-card-meta">
              Duration: ${service.duration} min
              ${
                typeof service.price !== "undefined"
                  ? ` £ Price: £${service.price}`
                  : ""
              }
            </div>
          </div>

          <div class="url-label">Booking URL</div>
          <div class="url-box">${serviceUrl}</div>

          <div class="footer-note">
            Place this near your counter or waiting area for easy bookings.
          </div>
        </div>

        <script>
          window.onload = function() {
            window.focus();
            window.print();
          };
        </script>
      </body>
    </html>
  `;

    // Create hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    // Write our full styled HTML
    const doc = iframe.contentWindow!.document;
    doc.open();
    doc.write(printHtml);
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow!.focus();
      iframe.contentWindow!.print();

      // Cleanup
      setTimeout(() => {
        iframe.remove();
      }, 1500);
    };
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-4 pb-4 bg-white border-b border-gray-200">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back
        </Button>
        <h2 className="text-lg font-semibold text-gray-900">Service QR Code</h2>
      </div>

      {isQRLoading ? (
        <div className="space-y-6 ">
          <>
            {/* Service Information Skeleton */}
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-3 mb-3">
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={160} height={28} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={100} height={20} />
                <Skeleton variant="text" width={90} height={20} />
                <Skeleton variant="text" width={130} height={20} />
              </div>
            </div>

            {/* QR Code Display Skeleton */}
            <div className="text-center">
              <div className="max-w-md mx-auto">
                {/* Title */}
                <Skeleton
                  variant="text"
                  width={200}
                  height={32}
                  className="mx-auto mb-4"
                />

                {/* QR Canvas */}
                <div className="flex justify-center mb-4">
                  <Skeleton
                    variant="rectangular"
                    width={308}
                    height={308}
                    className="rounded-lg"
                  />
                </div>

                {/* "Scan to book" text */}
                <Skeleton
                  variant="text"
                  width={180}
                  height={20}
                  className="mx-auto mb-2"
                />

                {/* Service info box */}
                <div className="p-3 bg-gray-100 border rounded">
                  <Skeleton
                    variant="text"
                    width={160}
                    height={28}
                    className="mx-auto"
                  />
                  <Skeleton
                    variant="text"
                    width={220}
                    height={20}
                    className="mx-auto"
                  />
                </div>

                {/* Booking URL section */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <Skeleton variant="text" width={90} height={18} />
                    <Skeleton
                      variant="rectangular"
                      width={90}
                      height={28}
                      className="rounded-md"
                    />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={36}
                    className="rounded-md"
                  />
                </div>

                {/* iFrame section header */}
                <div className="mt-3 space-y-1 text-left">
                  <Skeleton variant="text" width={180} height={22} />
                  <Skeleton variant="text" width="80%" height={18} />
                </div>

                {/* HTML Embedded Code section */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <Skeleton variant="text" width={130} height={18} />
                    <Skeleton
                      variant="rectangular"
                      width={90}
                      height={28}
                      className="rounded-md"
                    />
                  </div>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={52}
                    className="rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* Instructions Skeleton */}
            <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
              <Skeleton
                variant="text"
                width={200}
                height={24}
                className="mb-2"
              />
              <Skeleton variant="text" width="90%" height={18} />
              <Skeleton variant="text" width="75%" height={18} />
              <Skeleton variant="text" width="85%" height={18} />
              <Skeleton variant="text" width="70%" height={18} />
            </div>
          </>
        </div>
      ) : (
        <div className="space-y-6">
          {!isSetupComplete ? (
            <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <h4 className="font-medium text-amber-900">Setup Incomplete</h4>
              </div>
              <p className="mb-3 text-sm text-amber-800">
                Complete all {serviceChecks.length} requirements before using
                the booking URL or QR code. ({passedCount}/
                {serviceChecks.length} done)
              </p>
              {serviceChecks.map((check) => (
                <div
                  key={check.id}
                  className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                    check.passed
                      ? "border-green-200 bg-green-50"
                      : "border-red-100 bg-red-50"
                  }`}
                >
                  <div className="mt-0.5 shrink-0">
                    {check.passed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-medium ${
                          check.passed ? "text-green-800" : "text-red-800"
                        }`}
                      >
                        {check.label}
                      </span>
                      {!check.passed && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500">
                          {check.settingsTab} settings
                        </span>
                      )}
                    </div>
                    <p
                      className={`text-xs mt-0.5 ${
                        check.passed ? "text-green-700" : "text-red-700"
                      }`}
                    >
                      {check.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Service Information */}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-6 h-6 border border-gray-300 rounded-full"
                    style={{ backgroundColor: service.color }}
                  />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {service.name}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Duration:</span>{" "}
                    {service.duration} minutes
                  </div>
                  <div>
                    <span className="font-medium">Price:</span> £{service.price}
                  </div>
                  <div>
                    <span className="font-medium">Public:</span>{" "}
                    {service.showOnPublicCalendar ? "Yes" : "No"}
                  </div>
                  <div>
                    <span className="font-medium">Room Required:</span>{" "}
                    {service.roomRequired ? "Yes" : "No"}
                  </div>
                </div>
              </div>

              {/* QR Code Display */}
              <div id="qr-code-print-area" className="text-center">
                <div className="max-w-md mx-auto qr-container">
                  <h1 className="mb-4 text-xl font-bold text-gray-900">
                    {service.name}
                  </h1>

                  {/* QR Code */}
                  <div className="flex justify-center mb-4">
                    <canvas
                      ref={canvasRef}
                      className="border-4 border-gray-900 rounded-lg"
                    />
                  </div>

                  <div className="mt-3 mb-2">
                    <Button
                      variant="outline"
                      onClick={handleDownloadQR}
                      disabled={!qrCodeDataUrl}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download QR Code
                    </Button>
                  </div>

                  <div className="service-info">
                    <p className="mb-2 text-sm text-gray-600">
                      Scan to book this service
                    </p>
                    <div className="p-3 bg-gray-100 border rounded">
                      <p className="text-lg font-semibold text-gray-900">
                        {service.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        Duration: {service.duration} min • Price: £
                        {service.price}
                      </p>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-700">
                          Booking URL:
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleCopyUrlWithServideId(service, "bookingurl")
                          }
                          className="text-xs h-7"
                        >
                          {urlCopied ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 mr-1" />
                              Copy URL
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="p-2 font-mono text-xs text-gray-500 break-all border rounded url bg-gray-50">
                        {qrData}
                      </div>
                      <div className="mt-3 text-left">
                        <h2 className="font-medium text-gray-700 text-md">
                          iFrame Embedded Code:
                        </h2>
                        <label className="text-xs font-medium text-gray-700">
                          Embed the booking widget for this service directly
                          into your website:
                        </label>
                      </div>
                      <div className="flex items-center justify-between mt-3 mb-1">
                        <label className="text-xs font-medium text-gray-700">
                          HTML Embedded Code:
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleCopyUrlWithServideId(service, "iframe")
                          }
                          className="text-xs h-7"
                        >
                          {iframeCopied ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 mr-1" />
                              Copy URL
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="p-2 font-mono text-xs text-gray-500 break-all border rounded url bg-gray-50">
                        {generateIframeCodeWithServiceId(service)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <h4 className="mb-2 font-medium text-blue-900">
                  How to use this QR Code:
                </h4>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li>• Print this QR code and display it in your pharmacy</li>
                  <li>• Customers can scan it with their phone camera</li>
                  <li>
                    • They'll be taken directly to the booking page for this
                    service
                  </li>
                  <li>
                    • The QR code contains the direct link to book "
                    {service.name}"
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <Button variant="outline" onClick={onBack}>
          Go Back
        </Button>
        {isSetupComplete && (
          <>
            <Button
              variant="outline"
              onClick={handleDownloadQR}
              disabled={!qrCodeDataUrl}
            >
              <Download className="w-4 h-4 mr-2" />
              Download QR Code
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print QR Code
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function TemplateSelector({
  label,
  value,
  templateType = "email",
  actionType,
  onChange,
}: {
  label: string;
  value: string;
  templateType?: "email" | "sms";
  actionType: number;
  onChange: (val: string) => void;
}) {
  const { templates } = useServiceSettingsData();
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);

  const typeMap: Record<"email" | "sms", number> = { email: 0, sms: 1 };
  const uniqueTemplates = Array.from(
    new Map(templates.map((t) => [t.id, t])).values(),
  );
  const filteredTemplates = uniqueTemplates.filter(
    (t) =>
      Number(t.type) === typeMap[templateType] &&
      Number(t.actionType) === Number(actionType),
  );
  useEffect(() => {
    if (templates.length === 0) return;
    const templateToSet = value
      ? filteredTemplates.find((t) => t.name === value) || null
      : null;
    setSelectedTemplate(templateToSet);
  }, [filteredTemplates, value, templates.length]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select
        value={
          selectedTemplate?.id || (filteredTemplates.length > 0 ? "none" : "")
        }
        onValueChange={(templateId) => {
          if (templateId === "none") {
            setSelectedTemplate(null);
            onChange("");
            return;
          }
          const template = filteredTemplates.find((t) => t.id === templateId);
          if (template) {
            setSelectedTemplate(template);
            onChange(template.name); // Use id instead of name & Changed from name to id
          }
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select a template" />
        </SelectTrigger>
        <SelectContent>
          {filteredTemplates.length > 0 && (
            <SelectItem key="none" value="none">
              No default template
            </SelectItem>
          )}
          {filteredTemplates.length > 0 ? (
            filteredTemplates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                <div className="flex items-center gap-2">
                  {template.name}
                  {template.isDefault && (
                    <Badge variant="secondary" className="text-xs">
                      Default
                    </Badge>
                  )}
                </div>
              </SelectItem>
            ))
          ) : (
            <SelectItem value="" disabled>
              No {templateType} templates available
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      {selectedTemplate && (
        <div className="flex items-center justify-between p-3 border rounded bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              {selectedTemplate.name}
            </span>
            <Badge variant="outline" className="text-xs">
              {templateType.toUpperCase()}
            </Badge>
            {selectedTemplate.isDefault && (
              <Badge variant="secondary" className="text-xs">
                Default Template
              </Badge>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(true)}
            className="h-auto p-1 text-gray-600 hover:text-gray-900"
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      )}
      {showPreview && selectedTemplate && (
        <TemplatePreviewModal
          template={{
            id: selectedTemplate.id,
            name: selectedTemplate.name,
            type: templateType,
            subject: selectedTemplate.subject,
            content: selectedTemplate.content,
            actionType: selectedTemplate.actionType,
          }}
          templateType={templateType}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
function TemplatePreviewModal({
  template,
  templateType,
  onClose,
}: TemplatePreviewModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Template Preview</h3>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            ←
          </Button>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="font-medium">{template.name}</span>
            <Badge variant="outline">{templateType.toUpperCase()}</Badge>
          </div>
          {template.subject && (
            <div>
              <Label className="text-sm font-medium">Subject:</Label>
              <p className="p-2 text-sm border rounded bg-gray-50">
                {template.subject}
              </p>
            </div>
          )}
          <div>
            <Label className="text-sm font-medium">Content:</Label>
            <div className="p-4 text-sm whitespace-pre-wrap border rounded bg-gray-50">
              {template.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
